import { MoreVert } from "@mui/icons-material";
import {
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { differenceInDays } from "date-fns";
import {
  FEED_EVENT_REJECTION_PERIOD_DAYS,
  FERTILIZER_TOTAL_AMOUNT_UNIT,
  FERTILIZER_TYPES,
  FertilizerEvent,
  useArableFields,
  useFields,
} from "model";
import { useAppStore } from "stores";
import { getFormattedDate } from "../../common/utils/getFormattedTime";

import { useState } from "react";

import FertiliserIcon from "common/icons/FertiliserIcon";
import UpdateFertilizerEventDialog from "./UpdateFertilizerEventDialog";

export const convertToFertilizerAmountString = (
  amount: number,
  locale: Intl.LocalesArgument,
  unit: FERTILIZER_TOTAL_AMOUNT_UNIT,
  t: (a: string) => string
) => {
  let unitType = unit;

  if (amount > 1000 && unit === FERTILIZER_TOTAL_AMOUNT_UNIT.KG) {
    amount = amount / 1000;
    unitType = "TONNE" as FERTILIZER_TOTAL_AMOUNT_UNIT;
  } else if (amount > 1000 && unit === FERTILIZER_TOTAL_AMOUNT_UNIT.GALLONS) {
    amount = amount * 0.00378541;
    unitType = "M3" as FERTILIZER_TOTAL_AMOUNT_UNIT;
  }
  return (
    amount.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }) +
    " " +
    // totalAmount is an absolute total, so use the absolute unit label
    // ("kg") rather than the per-hectare rate label ("kg/ha").
    t(`fertilizerEvent.totalAmountUnit.totalOption.${unitType}`)
  );
};

export const getFertilizerTypeString = (
  fertilizerEvent: FertilizerEvent,
  t: (a: string) => string
) => {
  let fertilizerType = "";

  if (
    fertilizerEvent.fertilizerProperties.fertilizerType ===
    FERTILIZER_TYPES.ORGANIC
  ) {
    fertilizerType = t(
      `fertilizerEvent.organicFertilizerType.label.${fertilizerEvent.fertilizerProperties.organicFertilizerType}`
    );
  } else if (
    fertilizerEvent.fertilizerProperties.fertilizerType ===
    FERTILIZER_TYPES.SYNTHETIC
  ) {
    fertilizerType = `Synthetic (${fertilizerEvent.fertilizerProperties.productName})`;
  } else {
    fertilizerType = t(
      `fertilizerEvent.fertilizerType.label.${fertilizerEvent.fertilizerProperties.fertilizerType}`
    );
  }

  return fertilizerType;
};

export default function FertilizerEventListItem({
  fertilizerEvent,
}: {
  fertilizerEvent: FertilizerEvent;
}) {
  const { t } = useTranslation();
  const locale = useAppStore((state) => state.locale);

  const icon = <FertiliserIcon />;

  const formattedTime = getFormattedDate(
    fertilizerEvent.fertilizerApplicationDate,
    locale
  );
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Use React Query for fields data
  const { data: allGrassFields = [] } = useFields();
  const { data: allArableFields = [] } = useArableFields();

  const selectedGrassField = allGrassFields.filter((grassField) =>
    fertilizerEvent.fieldGuids.includes(grassField.guid)
  );

  const selectedArableFields = allArableFields.filter((arableField) =>
    fertilizerEvent.fieldGuids.includes(arableField.guid)
  );

  const fieldNames = [
    selectedArableFields.map((field) => field.name),
    selectedGrassField.map((field) => field.name),
  ]
    .flat()
    .join(", ");
  function openMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setMenuAnchor(null);
  }
  const fertilizerAmountString = convertToFertilizerAmountString(
    fertilizerEvent.totalAmount,
    locale,
    fertilizerEvent.totalAmountUnit,
    t
  );

  const fertilizerType = getFertilizerTypeString(fertilizerEvent, t);

  const canEdit =
    differenceInDays(
      Date.now(),
      fertilizerEvent.creationDate
        ? new Date(fertilizerEvent.creationDate)
        : Date.now()
    ) <= FEED_EVENT_REJECTION_PERIOD_DAYS;

  return (
    <ListItem key={`re-listitem-${fertilizerEvent.guid}`}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText
        primary={
          <Typography component="span" display="block">
            {t("fertilizerEvent.fertilizerEventDate", {
              fertilizerEventDate: formattedTime,
            })}
          </Typography>
        }
        secondary={
          <>
            <Typography component="span" display="block" noWrap>
              {fertilizerType}
            </Typography>
            <Typography component="span" display="block" noWrap>
              {t("fertilizerEvent.field.name")}
              {fieldNames}
            </Typography>

            <Typography component="span" display="block" noWrap>
              {fertilizerAmountString}
            </Typography>

            {fertilizerEvent.note && (
              <Typography
                component="span"
                display="block"
                textOverflow="ellipsis"
                overflow="auto"
              >
                Note: {fertilizerEvent.note}
              </Typography>
            )}
          </>
        }
      />
      {canEdit && (
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            aria-haspopup="true"
            onClick={openMenu}
            id={fertilizerEvent.guid}
            size="large"
          >
            <MoreVert />
          </IconButton>
          {
            <UpdateFertilizerEventDialog
              closeMenu={closeMenu}
              menuAnchor={menuAnchor}
              fertilizerEvent={fertilizerEvent}
            />
          }
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
}
